/**
 * The Cloudflare Turnstile widget (RidgeWill/account#112).
 *
 * A port of the login app's `components/Turnstile.tsx` in RidgeWill/auth, which
 * explains each choice at length. The short version, because every one of them
 * is a bug that already shipped once over there:
 *
 *   THE SITE KEY IS A PROP, read at runtime from the account deployment's
 *   `/providers`. A build-time key records what was true when CI ran.
 *
 *   EXPIRY AND FAILURE ARE REPORTED, as `onToken(null)`. A token is single use
 *   and short-lived, and a form that only hears about success submits a stale
 *   one.
 *
 *   `resetSignal` MINTS A FRESH TOKEN after a submit that spent one. Clearing
 *   the caller's copy alone left the login form permanently unsubmittable after
 *   a second wrong password, because the widget had no reason to fire again.
 *
 * ── ONE DIFFERENCE: THIS PORTAL HAS A DARK MODE ──────────────────────
 *
 * The login app pins `theme: 'light'` because it has none. Here the theme is
 * the portal's own choice (lib/theme.ts): an explicit Light or Dark sets
 * `data-theme` on the root and the widget follows it; System leaves it absent
 * and Cloudflare's `auto` follows the OS, which is what System means.
 */
import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
      reset: (id: string) => void;
    };
  }
}

/** Once per page however many widgets mount; two script tags double-render. */
let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile script failed')));
      return;
    }
    const el = document.createElement('script');
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => {
      // Let a later mount try again rather than inheriting a failure forever.
      scriptPromise = null;
      reject(new Error('turnstile script failed'));
    };
    document.head.appendChild(el);
  });
  return scriptPromise;
}

function widgetTheme(): 'light' | 'dark' | 'auto' {
  const chosen = document.documentElement.getAttribute('data-theme');
  return chosen === 'light' || chosen === 'dark' ? chosen : 'auto';
}

export function Turnstile({
  siteKey,
  onToken,
  action,
  resetSignal = 0,
}: {
  siteKey: string;
  /** A token, or null when there is none to send: not yet, expired, or failed. */
  onToken: (token: string | null) => void;
  /** Diagnostic only; the server pins hostname, not action. */
  action?: string;
  /** Bump after every submit that spent a token. See the file header. */
  resetSignal?: number;
}) {
  const host = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  /** Held in a ref so a parent's inline callback does not re-render the widget
   *  and throw away a solved challenge. */
  const cb = useRef(onToken);
  cb.current = onToken;

  useEffect(() => {
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !host.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(host.current, {
          sitekey: siteKey,
          action,
          theme: widgetTheme(),
          callback: (token: string) => cb.current(token),
          'expired-callback': () => cb.current(null),
          'error-callback': () => cb.current(null),
        });
      })
      // Fails closed: no token, so the form stays unsubmittable, and the server
      // would refuse anyway.
      .catch(() => cb.current(null));

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* already gone */
        }
        widgetId.current = null;
      }
    };
  }, [siteKey, action]);

  useEffect(() => {
    // `0` is the first render, not a reset.
    if (!resetSignal) return;
    if (widgetId.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetId.current);
      } catch {
        /* the unmount path handles a widget that is already gone */
      }
    }
    // No valid token between the reset and the next callback.
    cb.current(null);
  }, [resetSignal]);

  /* Centred: Cloudflare's iframe is fixed-width and sits hard left otherwise. */
  return <div ref={host} style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }} />;
}
